import { Dispatch, SetStateAction, useMemo } from 'react';

import { Box, Button, Typography } from '@mui/material';
import { Formik } from 'formik';
import * as Yup from 'yup';

import { ContentModal } from 'components/modal';
import { DreConfigConfiguracoesModalForm } from 'pages/financeiro/dre/configuracao/components';
import { useDreConfigConfiguracoesContext, useDreConfigContext } from 'pages/financeiro/dre/configuracao/contexts';
import { useMutateConfiguracao } from 'pages/financeiro/dre/configuracao/hooks';
import { DreConfiguracao, DreSubgrupo } from 'pages/financeiro/dre/configuracao/types';

type DreConfigConfiguracoesModal = {
    configuracao?: DreConfiguracao;
    open: boolean;
    setOpen: Dispatch<SetStateAction<boolean>>;
};

export function DreConfigConfiguracoesModal({ configuracao, open, setOpen }: DreConfigConfiguracoesModal) {
    const { activeSubgrupo, activeGrupo } = useDreConfigContext();
    const { configuracoes } = useDreConfigConfiguracoesContext();
    const { isLoading, onSubmitForm } = useMutateConfiguracao({ setOpen });
    const isImpostos = activeGrupo?.dreMercadologicaTipoGrupoExibicaoEnum === 'IMPOSTOS';
    const isDespesas = activeGrupo?.dreMercadologicaTipoGrupoExibicaoEnum === 'DESPESAS';
    const isCustos = activeGrupo?.dreMercadologicaTipoGrupoExibicaoEnum === 'CUSTOS';

    const validationSchema = useMemo(
        () =>
            Yup.object({
                dsNomeExibicao: Yup.string().required(),
                dsNomeVariavel: Yup.string().required(),

                dreTipoPreenchimento: Yup.object()
                    .nullable()
                    .test('is-required-despesas-custos', 'O campo é obrigatório.', (value) => Boolean((!isDespesas && !isCustos) || value)),

                tipoRegimeTributario: Yup.object()
                    .nullable()
                    .test('is-required-impostos', 'O campo é obrigatório.', (value) => Boolean(!isImpostos || value)),
            }),
        [],
    );

    /**
     * Retorna o próximo número de sequência disponível para a configuração.
     */
    const nextNrSequencia = useMemo(() => {
        const sequencias = configuracoes.map((item) => item.nrSequencia);
        const maxSequencia = Math.max(...sequencias, 0);

        return Math.ceil((maxSequencia + 1) / 10) * 10 + 1;
    }, [configuracoes]);

    const initialValues = useMemo(
        (): DreConfiguracao => ({
            nrSequencia: nextNrSequencia,
            idDreMercadologicaConfiguracao: 0,
            dsObservacao: '',
            dsValorConfig: '',
            dsNomeVariavel: '',
            dsNomeExibicao: '',
            fgAtivo: true,
            fgTotalizar: false,
            fgRecorrente: false,
            fgPercentual: false,
            fgObrigatorio: false,
            dreTipoPreenchimento: null,
            tipoRegimeTributario: null,
            dreMercadologicaGrupoExibicao: activeSubgrupo as DreSubgrupo,

            color: {
                background: '',
                text: '',
            },

            dreMercadologicaConfiguracaoCategorizacao: [
                {
                    divisao: null,
                    grupo: null,
                    subgrupo: null,
                    secao: null,
                },
            ],
        }),
        [activeSubgrupo, nextNrSequencia],
    );

    return (
        <ContentModal
            open={open}
            setOpen={setOpen}
            sx={{
                display: 'flex',
                maxHeight: '90vh',
                flexDirection: 'column',
                borderRadius: 1,
                gap: 2,
                padding: 2,
                width: { xs: '90dvw', md: 700 },
            }}
        >
            <Typography sx={{ color: 'secondary.main', fontSize: '18px' }}>
                {configuracao ? 'Editar configuração' : 'Adicionar configuração'}
            </Typography>

            <Formik
                validateOnChange={false}
                onSubmit={onSubmitForm}
                validationSchema={validationSchema}
                initialValues={configuracao || initialValues}
            >
                {({ submitForm }) => (
                    <>
                        <DreConfigConfiguracoesModalForm />

                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                            <Button size="small" variant="outlined" onClick={() => setOpen(false)}>
                                Fechar
                            </Button>

                            <Button variant="contained" size="small" onClick={submitForm} loading={isLoading}>
                                Salvar
                            </Button>
                        </Box>
                    </>
                )}
            </Formik>
        </ContentModal>
    );
}
